/*
 * AI-GENERATED
 * Microsoft 365 Copilot
 * LLM: GPT-5 reasoning
 * Tarkistanut Arttu Pietarinen
 * 2026-09-15
 */
import strings from '../../../translations';
import { useAppSelector } from '../../../state/hooks';
import { useEffect, useState, useMemo } from 'react';
import styled from 'styled-components';
import { motion } from 'motion/react';
import { faAngleDown, faAngleUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { dropdownVariants } from '../utils/SearchUtil';
import useIndexSearch from '../utils/useIndexSearch';

const StyledDropDown = styled(motion.div)`
  width: 100%;
  padding: 0 1px;
  overflow: auto;
  background-color: ${(props) => props.theme.colors.mainWhite};
`;
const StyledItemButton = styled.button`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  margin-top: 0.5em;
  padding: 0.5em 1em;
  border: 0;
  border-radius: 5px;
  color: ${(props) => props.theme.colors.mainWhite};
  background-color: ${(props) => props.theme.colors.mainColor1};
  cursor: pointer;
  text-align: left;
`;
const StyledTitle = styled.span`
  overflow: hidden;
  font-size: 14px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const StyledAttributes = styled.dl`
  margin: 0;
`;
const StyledAttribute = styled.div`
  display: grid;
  grid-template-columns: minmax(100px, 1fr) minmax(0, 2fr);
  gap: 0.5em;
  padding: 4px;
  border-bottom: 1px solid #ccc;
`;
const StyledKey = styled.dt`
  overflow: hidden;
  font-weight: 600;
  text-overflow: ellipsis;
`;
const StyledValue = styled.dd`
  overflow-wrap: anywhere;
  margin: 0;
`;

const StyledHighlight = styled.mark`
  padding: 0 2px;
  border-radius: 2px;
  color: inherit;
  background-color: ${(props) =>
    props.theme.colors.secondaryColorYellow ?? '#ffeb3b'};
  font-weight: 600;
`;

const StyledMessage = styled.div`
  margin-top: 8px;
  text-align: center;
`;
const StyledError = styled(StyledMessage)`
  color: ${(props) => props.theme.colors.secondaryColorDarkOrange || '#c55'};
`;

const StyledShowMoreButtonWrapper = styled.div`
  text-align: center;
  margin-top: 1em;
`;

const StyledShowMoreButton = styled.button`
  width: 250px;
  height: 35px;
  color: ${(props) => props.theme.colors.mainWhite};
  background-color: ${(props) => props.theme.colors.mainColor1};
  border-radius: 20px;
  box-shadow: 0px 1px 3px #0000001f;
  border: none;
`;

const getItemKey = (item) => `${item.searchMetadataId}:${item.entityId}`;
const getAttributes = (item) => item.fulltextJson ?? {};
const getTitle = (item) => {
  const attributes = getAttributes(item);
  return (
    attributes.nimi ??
    attributes.name ??
    attributes.title ??
    attributes.oid ??
    item.entityId
  );
};
const formatValue = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  return typeof value === 'object' ? JSON.stringify(value) : String(value);
};

const IndexSearchResultPanel = ({ onResultSelect }) => {
  const {
    status = 'idle',
    query = '',
    items = [],
    error = null,
    paging = { limit: 100, offset: 0, returned: 0 }
  } = useAppSelector((state) => state.rpc.indexSearchResults);

  const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const highlightQuery = (value, query) => {
    const formattedValue = formatValue(value);
    const normalizedQuery = query.trim();

    if (!normalizedQuery || formattedValue === '-') {
      return formattedValue;
    }

    const pattern = new RegExp(`(${escapeRegExp(normalizedQuery)})`, 'gi');

    return formattedValue.split(pattern).map((part, index) => {
      const isMatch =
        part.localeCompare(normalizedQuery, undefined, {
          sensitivity: 'accent'
        }) === 0;

      if (!isMatch) {
        return part;
      }

      return <StyledHighlight key={`${index}-${part}`}>{part}</StyledHighlight>;
    });
  };

  const {
    //indexSearchResults,
    //searchOn,
    channel
    //selectedLayersByType,
    //lastSearchValue,
    //lastSearchAttribute
  } = useAppSelector((state) => state.rpc);

  const { loadNextPage } = useIndexSearch();

  const [openItemKey, setOpenItemKey] = useState(null);

  const toggleItem = (item) => {
    const itemKey = getItemKey(item);
    const nextItemKey = openItemKey === itemKey ? null : itemKey;
    setOpenItemKey(nextItemKey);
    if (nextItemKey) onResultSelect?.(item);
  };

  useEffect(() => {
    console.info('items', items);
    console.info('status', status);
    console.info('query', query);
    console.info('error', error);
  }, [items, status, query, error]);

  const hasNextPage =
    status === 'succeeded' &&
    paging.returned > 0 &&
    paging.returned === paging.limit;

  const SEARCH_RESULTS_LAYER_ID = 'feature-search-results';

  const showFeaturesOnMap = (channel, geoJson) => {
    channel?.postRequest('MapModulePlugin.RemoveFeaturesFromMapRequest', [
      null,
      null,
      SEARCH_RESULTS_LAYER_ID
    ]);

    if (geoJson.features.length === 0) {
      return;
    }

    channel?.postRequest('MapModulePlugin.AddFeaturesToMapRequest', [
      geoJson,
      {
        layerId: SEARCH_RESULTS_LAYER_ID,
        centerTo: true,
        maxZoomLevel: 13,
        cursor: 'pointer',
        featureStyle: {
          fill: {
            color: 'rgba(229, 0, 131, 1)'
          },
          stroke: {
            color: 'rgba(229, 0, 131, 1)',
            width: 5,
            lineDash: 'solid',
            lineJoin: 'round'
          },
          image: {
            shape: 2,
            size: 5,
            fill: {
              color: 'rgba(229, 0, 131, 1)'
            }
          }
        }
      }
    ]);
  };

  const createFeatureCollection = (items) => ({
    type: 'FeatureCollection',
    features: items
      .filter((item) => item.geometry !== null && item.geometry !== undefined)
      .map((item) => ({
        type: 'Feature',
        id: item.id,
        geometry: item.geometry,
        properties: {
          entityId: item.entityId,
          entityType: item.entityType
        }
      }))
  });

  //show items on map
  const searchResultGeoJson = useMemo(
    () => createFeatureCollection(items ?? []),
    [items]
  );

  useEffect(() => {
    if (!channel) {
      return;
    }
    console.info('create geojson', searchResultGeoJson);

    const start = performance.now();

    showFeaturesOnMap(channel, searchResultGeoJson);
    const durationMs = performance.now() - start;

    console.log(`show on map called ${durationMs.toFixed(2)} ms`);
  }, [channel, searchResultGeoJson]);

  // // Effect to handle warnings and map display
  // useEffect(() => {
  //   if (items && items.length > 0 && items.some((item) => item.geometry)) {
  //     // if (
  //     //   indexSearchResults.some((layer) => layer.limitExceeded) &&
  //     //   !showWarn
  //     // ) {
  //     //   setShowWarn(true);
  //     // } //TODO show warnings
  //     showFeaturesOnMap(channel, createFeatureCollection);
  //   } else {
  //     //setShowWarn(false);
  //   }
  // }, [channel, items]);

  return (
    <>
      <StyledDropDown
        variants={dropdownVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        transition="transition"
      >
        {error && <StyledError role="alert">{error}</StyledError>}
        {items.map((item) => {
          const itemKey = getItemKey(item);
          const isOpen = openItemKey === itemKey;
          const detailsId = `index-search-result-${itemKey}`;
          return (
            <div key={itemKey}>
              <StyledItemButton
                type="button"
                aria-expanded={isOpen}
                aria-controls={detailsId}
                onClick={() => toggleItem(item)}
              >
                <StyledTitle>{getTitle(item)}</StyledTitle>
                <FontAwesomeIcon icon={isOpen ? faAngleUp : faAngleDown} />
              </StyledItemButton>
              {isOpen && (
                <StyledAttributes id={detailsId}>
                  {Object.entries(getAttributes(item)).map(([key, value]) => (
                    <StyledAttribute key={key}>
                      <StyledKey>{key}</StyledKey>
                      <StyledValue>{highlightQuery(value, query)}</StyledValue>
                    </StyledAttribute>
                  ))}
                </StyledAttributes>
              )}
            </div>
          );
        })}
        {status === 'loading' && (
          <StyledMessage role="status">
            {strings.search?.searching ?? 'Searching...'}
          </StyledMessage>
        )}
        {status === 'succeeded' && query && items.length === 0 && (
          <StyledMessage>{strings.search.feature.noResults}</StyledMessage>
        )}
      </StyledDropDown>

      {hasNextPage && (
        <StyledShowMoreButtonWrapper>
          <StyledShowMoreButton onClick={() => loadNextPage()}>
            {strings.gfi.getMoreFeatures}
          </StyledShowMoreButton>
        </StyledShowMoreButtonWrapper>
      )}
    </>
  );
};

export default IndexSearchResultPanel;
